import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { SharedVariablesService } from '../shared-variables/shared-variables.service';

/**
 * Loads the Google Maps SDK's `maps` library, once per page.
 *
 * Both map paths use this: `pozi-google-maps` and `base-tracking-map` each used to build
 * their own `Loader` with byte-identical options. `js-api-loader` dedupes only while the
 * options match and *throws* when they differ, so that duplication worked by coincidence
 * and any divergence would have become a runtime error.
 *
 * The `Loader` is built on first `load()`, never at injection time: the API key is seeded
 * at app startup by `system/v1/info`, so it has to be read on first use.
 */
@Injectable({ providedIn: 'root' })
export class GoogleMapsLoaderService {
  private sharedVariablesService = inject(SharedVariablesService);
  private locale = inject(LOCALE_ID);

  private mapsLibrary: Promise<google.maps.MapsLibrary> | undefined;

  /** Resolves once the `maps` library is available. Never rejects — a load failure is logged. */
  load(): Promise<void> {
    this.mapsLibrary ??= this.importMapsLibrary();

    return this.mapsLibrary.then(
      () => undefined,
      (error) => console.error(error)
    );
  }

  /**
   * `async` on purpose: `Loader.importLibrary` ends in a bare `google.maps`
   * read, and the Loader skips its inline bootstrap whenever it believes the
   * SDK is already set up (script element present / `done` singleton state) —
   * so if the `google` namespace is gone by then (spec shims delete it in
   * `afterEach`; the script never executes under jsdom), that read throws a
   * *synchronous* `ReferenceError`. The async wrapper turns any synchronous
   * Loader throw into a rejection that `load()`'s error handler logs, keeping
   * the never-rejects contract everywhere (TODO-69's flake fix).
   */
  private async importMapsLibrary(): Promise<google.maps.MapsLibrary> {
    return new Loader({
      apiKey: this.sharedVariablesService.getVar('googleMapsApiKey'),
      version: 'weekly',
      language: this.locale,
      region: this.locale === 'hu' ? 'HU' : 'US',
    }).importLibrary('maps');
  }
}
